import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { capitalAmortizationService } from '../../services/capitalAmortizationService';
import { CapitalAmortization } from '../../types/CapitalAmortization';
import { formatCurrency } from '../../utils/format';
import '../../pages/Amortizaciones.css';

interface AmortizacionesListProps {
  investorId?: string;
  sheetId?: string;
  /** Si es true no se muestra nada cuando no hay amortizaciones */
  hideWhenEmpty?: boolean;
  /** Clase adicional del contenedor (p.ej. detail-section en el detalle del inversor) */
  className?: string;
}

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

/** Amortizaciones anticipadas de un inversor o de una ficha (solo lectura). */
const AmortizacionesList: React.FC<AmortizacionesListProps> = ({ investorId, sheetId, hideWhenEmpty, className }) => {
  const [items, setItems] = useState<CapitalAmortization[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const request = sheetId
      ? capitalAmortizationService.getBySheet(sheetId)
      : investorId ? capitalAmortizationService.getByInvestor(investorId) : null;
    if (!request) return;
    request.then(res => setItems(res.data)).catch(() => setItems([])).finally(() => setLoaded(true));
  }, [investorId, sheetId]);

  if (!loaded || (hideWhenEmpty && items.length === 0)) return null;

  const lineFor = (a: CapitalAmortization) => (sheetId ? a.lines.filter(l => l.investmentSheetId === sheetId) : a.lines);
  const sum = (a: CapitalAmortization, key: 'capitalAmortizado' | 'capitalRestante' | 'neto') =>
    lineFor(a).reduce((acc, l) => acc + (l[key] ?? 0), 0);

  return (
    <div className={`amz-list ${className ?? ''}`}>
      <h4>Amortizaciones anticipadas</h4>
      {items.length === 0 ? (
        <p className="amz-muted">Sin amortizaciones anticipadas.</p>
      ) : (
        <div className="amz-table-wrap">
          <table className="amz-table">
            <thead>
              <tr>
                <th>Fecha de corte</th>
                {!sheetId && <th>Producto</th>}
                <th>Estado</th>
                <th className="num">Amortizado</th>
                <th className="num">Restante</th>
                <th className="num">Neto intereses</th>
                <th>Resumen</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id}>
                  <td>{fmtDate(a.fechaCorte)}</td>
                  {!sheetId && <td>{a.productId} · {a.productName}</td>}
                  <td><span className={`amz-badge amz-badge-${a.estado.toLowerCase()}`}>{a.estado}</span></td>
                  <td className="num">{formatCurrency(sum(a, 'capitalAmortizado'))}</td>
                  <td className="num">{formatCurrency(sum(a, 'capitalRestante'))}</td>
                  <td className="num">{formatCurrency(sum(a, 'neto'))}</td>
                  <td>
                    {a.resumen}
                    {a.operacionId && (
                      <> · <Link to={`/amortizaciones/${a.operacionId}`}>{a.operacionDescripcion ?? 'Ver operación'}</Link></>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AmortizacionesList;
